// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getUser } from "../auth/auth";
import { getSellerDisputes, respondToDispute } from "../api/disputes";
import { useToast } from "../components/ToastProvider";
import "../styles/SellerDisputes.css";
import {
  IMAGE_INPUT_ACCEPT_ATTR,
  MAX_IMAGE_BYTES_DEFAULT,
  validateImageFiles,
} from "../utils/imageUploadValidation";

export default function SellerDisputes() {
  const navigate = useNavigate();
  const user = getUser();
  const { showToast } = useToast();
  
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseForm, setResponseForm] = useState({
    message: "",
    evidence: []
  });
  const [responseError, setResponseError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    fetchDisputes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchDisputes = async () => {
    try {
      const data = await getSellerDisputes(user.id);
      setDisputes(data);
    } catch (err) {
      setError("Failed to load disputes");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "opened": return "#fbbf24";
      case "awaiting_seller_response": return "#f97316";
      case "seller_responded": return "#a855f7";
      case "under_review": return "#60a5fa";
      case "resolved_refund": return "#ef4444";
      case "resolved_partial": return "#fb923c";
      case "resolved_release_funds": return "#10b981";
      case "closed": return "#9ca3af";
      default: return "#9ca3af";
    }
  };

  const getStatusLabel = (status) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTimeRemaining = (deadline) => {
    const now = new Date();
    const end = new Date(deadline);
    const diff = end - now;
    
    if (diff <= 0) return "Deadline passed";
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h remaining`;
    }
    return `${hours}h ${minutes}m remaining`;
  };

  const handleRespond = (dispute) => {
    setSelectedDispute(dispute);
    setResponseForm({ message: "", evidence: [] });
    setResponseError("");
    setShowResponseModal(true);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      setResponseError("Maximum 5 photos allowed");
      e.target.value = "";
      return;
    }

    const { validFiles, errors } = validateImageFiles(files, { maxBytes: MAX_IMAGE_BYTES_DEFAULT });
    if (errors.length > 0) {
      setResponseError(errors.join(" "));
      e.target.value = "";
      return;
    }

    const filePromises = validFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(filePromises).then(results => {
      setResponseError("");
      setResponseForm({ ...responseForm, evidence: results });
    });
  };

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    
    if (!responseForm.message.trim()) {
      setResponseError("Please provide a response message");
      return;
    }

    setSubmitting(true);
    setResponseError("");

    try {
      await respondToDispute(selectedDispute.id, responseForm.message, responseForm.evidence);
      
      showToast("Response submitted successfully!", "success");
      setShowResponseModal(false);
      setSelectedDispute(null);
      
      // Refresh disputes
      await fetchDisputes();
    } catch (err) {
      setResponseError(err.message || "Failed to submit response. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="seller-disputes-page">
        <div className="loading">Loading disputes...</div>
      </div>
    );
  }

  return (
    <div className="seller-disputes-page">
      <div className="disputes-container">
        <div className="disputes-header">
          <button className="back-btn" onClick={() => navigate("/home")}>
            ← Back
          </button>
          <h1>My Disputes (Seller)</h1>
        </div>

        {error && <div className="error-message">{error}</div>}

        {disputes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">✓</div>
            <h2>No disputes</h2>
            <p>You don't have any active disputes at the moment.</p>
          </div>
        ) : (
          <div className="disputes-list">
            {disputes.map((dispute) => (
              <div key={dispute.id} className="dispute-card">
                <div className="dispute-header-row">
                  <div className="dispute-id">
                    <span className="dispute-label">Dispute</span>
                    <span className="dispute-number">{dispute.id}</span>
                  </div>
                  <div className="dispute-status" style={{ borderColor: getStatusColor(dispute.status) }}>
                    <span className="status-dot" style={{ background: getStatusColor(dispute.status) }}></span>
                    {getStatusLabel(dispute.status)}
                  </div>
                </div>

                <div className="dispute-content">
                  <div className="dispute-details">
                    <h3 className="dispute-title">Order #{dispute.orderId}</h3>
                    <div className="issue-type">
                      <strong>Issue:</strong> {dispute.issueType}
                    </div>
                    <div className="buyer-description">
                      <strong>Buyer's description:</strong>
                      <p>{dispute.description}</p>
                    </div>
                    
                    {dispute.evidence && dispute.evidence.length > 0 && (
                      <div className="evidence-section">
                        <strong>Buyer's evidence:</strong> {dispute.evidence.length} photo(s) attached
                      </div>
                    )}

                    <div className="dispute-meta">
                      <div className="meta-item">
                        <span className="meta-label">Filed:</span>
                        <span className="meta-value">
                          {new Date(dispute.createdAt).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      
                      {dispute.status === "opened" && (
                        <div className="meta-item deadline-warning">
                          <span className="meta-label">Response deadline:</span>
                          <span className="meta-value deadline">
                            {getTimeRemaining(dispute.sellerResponseDeadline)}
                          </span>
                        </div>
                      )}
                    </div>

                    {dispute.sellerResponse && (
                      <div className="seller-response-preview">
                        <strong>Your response:</strong>
                        <p>{dispute.sellerResponse.message}</p>
                        <span className="response-time">
                          Submitted {new Date(dispute.sellerResponse.submittedAt).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="dispute-actions">
                  {dispute.status === "opened" && !dispute.sellerResponse && (
                    <button className="btn-primary" onClick={() => handleRespond(dispute)}>
                      Respond to Dispute
                    </button>
                  )}
                  {dispute.sellerResponse && (
                    <div className="response-status">
                      ✓ Response submitted - awaiting admin review
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Response Modal */}
      {showResponseModal && selectedDispute && (
        <div className="modal-overlay" onClick={() => setShowResponseModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowResponseModal(false)}>×</button>
            
            <h2 className="modal-title">Respond to Dispute</h2>
            <p className="modal-subtitle">Dispute #{selectedDispute.id}</p>

            <div className="dispute-summary">
              <div><strong>Issue:</strong> {selectedDispute.issueType}</div>
              <div><strong>Buyer's claim:</strong></div>
              <p className="buyer-claim">{selectedDispute.description}</p>
            </div>

            <form className="response-form" onSubmit={handleSubmitResponse}>
              <div className="form-group">
                <label htmlFor="message">Your Response *</label>
                <textarea
                  id="message"
                  value={responseForm.message}
                  onChange={(e) => setResponseForm({ ...responseForm, message: e.target.value })}
                  placeholder="Explain your side of the situation..."
                  rows="6"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="evidence">Supporting Evidence (Photos)</label>
                <input
                  type="file"
                  id="evidence"
                  accept={IMAGE_INPUT_ACCEPT_ATTR}
                  multiple
                  onChange={handleFileUpload}
                  className="file-input"
                />
                {responseForm.evidence.length > 0 && (
                  <div className="evidence-preview">
                    {responseForm.evidence.length} photo(s) selected
                  </div>
                )}
              </div>

              <div className="response-info">
                <strong>Tips for responding:</strong>
                <ul>
                  <li>Be honest and provide all relevant details</li>
                  <li>Include photos if they support your case</li>
                  <li>Describe what happened from your perspective</li>
                  <li>Offer a resolution if appropriate (partial refund, etc.)</li>
                </ul>
              </div>

              {responseError && <div className="form-error">{responseError}</div>}

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setShowResponseModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit Response"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
